import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';
import isArray from 'lodash/isArray';
import { TogglePanelCollection, TogglePanel } from '@els/els-react--toggle-panel';
import { searchSectionOpenedPanels as searchSectionOpenedPanelsSelector } from '@Pages/QueryBuilder/QuerySelector';
import { setSearchSectionOpenedPanel as setSearchSectionOpenedPanelAction } from '@Pages/QueryBuilder/QueryletActions';
import DragNode from '../DragNode/DragNode';
import HistoryDragNode from '../DragNode/HistoryDragNode';
import FormDragNode from '../DragNode/FormDragNode';

function isDragNode(node) {
  return node && (node.type === 'fact' || node.type === 'field' || node.id === 'STRUCTURE.QL' || node.id === 'ADVANCED.QL');
}

function isHistoryDragNode(node) {
  return node && node.type === 'history' && !node.isOnlyOnce;
}

function isFormDragNode(node) {
  return node && node.type === 'form';
}

function SearchSectionNode({ node, level, index, isSearched, addToBuilder }) {
  const dispatch = useDispatch();
  const searchSectionPanelsOpen = useSelector(searchSectionOpenedPanelsSelector);
  const setOpenPanels = useMemo(() => {
    return isArray(node) ? searchSectionPanelsOpen.filter((panelId) => node.some((item) => item.id === panelId)) : [];
  }, [node, searchSectionPanelsOpen]);
  const panelOnCLick = (...args) => {
    dispatch(setSearchSectionOpenedPanelAction(...args));
  };

  return (
    <>
      {isArray(node) && (
        <TogglePanelCollection
          iconRight
          noTopKeyline
          noBottomKeyline
          noIndent
          headerLevel={String(level + 3)}
          id={`querybuilder-toggle-panel-collection-level-${level}-index-${index}`}
          className={cn(
            'query-builder-search-section__toggle-panel-collection',
            `query-builder-search-section__toggle-panel-collection--level-${level}`
          )}
          setOpenPanels={isSearched ? ['open all'] : setOpenPanels}>
          {node.map((_node) => (
            <TogglePanel
              key={_node.id}
              id={_node.id}
              data-automationId={`querybuilder-${_node.displayName}`}
              header={_node.displayName}
              headerLevel={String(level + 3)}
              onClick={panelOnCLick}>
              {_node.childrenCount &&
                _node.children.map((child, indexChild) => (
                  <SearchSectionNode
                    // eslint-disable-next-line react/no-array-index-key
                    key={`level-${level + 1}-index-${indexChild}`}
                    node={child}
                    level={level + 1}
                    index={indexChild}
                    addToBuilder={addToBuilder}
                  />
                ))}
            </TogglePanel>
          ))}
        </TogglePanelCollection>
      )}
      {isDragNode(node) && <DragNode node={node} addToBuilder={addToBuilder} />}
      {isHistoryDragNode(node) && <HistoryDragNode node={node} addToBuilder={addToBuilder} />}
      {isFormDragNode(node) && <FormDragNode node={node} addToBuilder={addToBuilder} />}
    </>
  );
}

SearchSectionNode.propTypes = {
  node: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  level: PropTypes.number,
  index: PropTypes.number,
  isSearched: PropTypes.bool,
  addToBuilder: PropTypes.func.isRequired,
};

export default SearchSectionNode;
